import React, { useState } from "react";
import { 
  FaShieldAlt, 
  FaEthereum, 
  FaHandshake, 
  FaLock, 
  FaMoneyBillWave,
  FaUserCheck,
  FaFileContract,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChartLine,
  FaArrowRight,
  FaQuestion,
  FaClipboardCheck,
  FaAngleDown,
  FaAngleUp,
  FaCheck
} from "react-icons/fa";
import { MdSecurity, MdVerifiedUser, MdOutlineHowToReg } from "react-icons/md";
import { Link } from "react-router-dom";

const HowItWorksPage = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [activeQuestion, setActiveQuestion] = useState(null);

  // Process steps data
  const steps = [
    {
      id: 1,
      title: "Create Agreement",
      description: "Define transaction terms, deadlines, and conditions for fund release.",
      icon: <FaFileContract className="text-white text-3xl" />,
      details: [
        "Select the type of transaction (goods, services, freelance work, etc.)",
        "Specify amount, currency, and payment terms",
        "Set deadline for delivery or fulfillment",
        "Define any additional conditions for fund release",
        "Invite the counterparty via email or wallet address"
      ]
    },
    {
      id: 2,
      title: "Fund Escrow",
      description: "Buyer deposits funds that are secured in a smart contract.",
      icon: <FaMoneyBillWave className="text-white text-3xl" />,
      details: [
        "Connect your preferred wallet (MetaMask, Trust Wallet, etc.)",
        "Choose from supported stablecoins (USDT, USDC) or crypto (ETH, BTC)",
        "The funds are locked in a secure smart contract on the blockchain",
        "Both parties receive confirmation of funding",
        "Escrow security deposit is fully visible on the blockchain"
      ]
    },
    {
      id: 3,
      title: "Delivery & Verification",
      description: "Seller provides goods/services, buyer verifies completion.",
      icon: <FaClipboardCheck className="text-white text-3xl" />,
      details: [
        "Seller completes the work or sends the goods as agreed",
        "Seller marks the transaction as 'Delivered' in the platform",
        "Buyer is notified to verify the delivery",
        "Buyer can request revisions if needed",
        "Both parties can communicate through the secure messaging system"
      ]
    },
    {
      id: 4,
      title: "Funds Released",
      description: "Upon approval, funds are automatically transferred to the seller.",
      icon: <FaCheckCircle className="text-white text-3xl" />,
      details: [
        "Buyer confirms that all terms have been met",
        "Smart contract automatically releases funds to the seller",
        "Transaction is recorded permanently on the blockchain",
        "Both parties can leave reviews and ratings",
        "Transaction history is maintained for future reference"
      ]
    }
  ];

  // Dispute resolution process
  const disputeSteps = [
    {
      id: 1,
      title: "Dispute Initiated",
      description: "Either party can raise a dispute if terms aren't met.",
      icon: <FaExclamationTriangle className="text-white text-3xl" />
    },
    {
      id: 2,
      title: "Evidence Submission",
      description: "Both parties submit evidence and explanations.",
      icon: <FaFileContract className="text-white text-3xl" />
    },
    {
      id: 3,
      title: "Arbiter Review",
      description: "Independent arbiters review the case and evidence.",
      icon: <FaHandshake className="text-white text-3xl" />
    },
    {
      id: 4,
      title: "Resolution & Release",
      description: "Arbiters make a decision and funds are released accordingly.",
      icon: <MdVerifiedUser className="text-white text-3xl" />
    }
  ];

  // FAQ data
  const faqData = [
    {
      id: 1,
      question: "What types of transactions can I use Escrownia for?",
      answer: "Escrownia is ideal for a wide range of transactions including freelance services, digital product sales, high-value purchases, international trade, real estate deposits, and cryptocurrency trades. Essentially, any situation where trust between parties is crucial can benefit from our escrow service."
    },
    {
      id: 2,
      question: "How secure is the platform?",
      answer: "Escrownia employs multi-layered security including smart contract audits, 2FA protection, wallet whitelisting, and private key encryption. All transactions are recorded on the blockchain, providing tamper-proof security. Our platform undergoes regular security audits and penetration testing by independent security firms."
    },
    {
      id: 3,
      question: "What currencies do you support?",
      answer: "We currently support major stablecoins (USDT, USDC) and cryptocurrencies (ETH, BTC). Stablecoins are recommended for longer-term transactions to avoid price volatility. Our smart contracts automatically handle currency conversions when needed."
    },
    {
      id: 4,
      question: "How much does it cost to use Escrownia?",
      answer: "Escrownia charges a small percentage fee based on the transaction amount, typically between 1-3%. The fee varies depending on the transaction type, value, and complexity. For detailed pricing, please visit our Pricing page. There are no hidden fees, and all costs are clearly displayed before confirming a transaction."
    },
    {
      id: 5,
      question: "How does dispute resolution work?",
      answer: "If a dispute arises, either party can initiate the resolution process. Both parties submit evidence and explanations. Our network of independent arbiters reviews the case and makes a fair decision based on the terms of the agreement and submitted evidence. Arbiters are selected based on expertise in the relevant field and have no conflicts of interest."
    },
    {
      id: 6,
      question: "Can I use Escrownia for international transactions?",
      answer: "Yes! Escrownia is designed for global use. Our blockchain-based platform eliminates traditional banking restrictions, currency conversion issues, and international transfer delays. Users from over 150 countries trust Escrownia for their cross-border transactions."
    }
  ];

  // Toggle FAQ question
  const toggleQuestion = (id) => {
    setActiveQuestion(activeQuestion === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white pt-24 pb-16">
      {/* Hero Section */}
      <section className="container mx-auto px-6 mb-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
            How Escrownia Works
          </h1>
          <p className="text-xl text-gray-300 mb-10">
            Our blockchain-based escrow service ensures secure, transparent, and trustless transactions between parties. 
            Learn how we're revolutionizing digital transactions through smart contracts.
          </p>
        </div>
        
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step) => (
            <div key={step.id} className="relative">
              {/* Connection line */}
              {step.id < steps.length && (
                <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-green-500 to-blue-600 -z-10 transform -translate-x-1/2"></div>
              )}
              
              {/* Step content */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-blue-500 transition-all duration-300 h-full flex flex-col">
                <div className="bg-gradient-to-br from-green-500 to-blue-600 w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-white text-2xl font-bold">{step.id}</span>
                </div>
                <h3 className="text-xl font-bold text-center mb-4">{step.title}</h3>
                <p className="text-gray-400 text-center mb-6">{step.description}</p>
                <ul className="mt-auto space-y-2">
                  {step.details.slice(0, 3).map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-300">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Security Features */}
      <section className="bg-gray-800 bg-opacity-50 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-6">Security & Transparency First</h2>
            <p className="text-gray-300">
              Our platform is built on blockchain technology, ensuring maximum security and complete transparency for all transactions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaShieldAlt className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Blockchain Security</h3>
              <p className="text-gray-400">
                All transactions are recorded on the blockchain, making them immutable and tamper-proof. Smart contracts ensure that funds are only released when predefined conditions are met.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <MdSecurity className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Smart Contract Audits</h3>
              <p className="text-gray-400">
                Our smart contracts undergo rigorous auditing by independent security firms to ensure they're free of vulnerabilities. We follow the highest security standards in the industry.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-green-500 transition-all duration-300">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaLock className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4">Multiple Security Layers</h3>
              <p className="text-gray-400">
                From 2FA protection to wallet whitelisting and private key encryption, we implement multiple security layers to protect your funds and personal information.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Dispute Resolution Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-6">Fair Dispute Resolution</h2>
            <p className="text-gray-300 mb-8">
              In the rare case of a dispute between parties, our platform offers a transparent and fair resolution process. Independent arbiters review the case and make an impartial decision.
            </p>
            
            <div className="space-y-8">
              {disputeSteps.map((step) => (
                <div key={step.id} className="flex items-start">
                  <div className="bg-gradient-to-br from-green-500 to-blue-600 w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white text-sm font-bold">{step.id}</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-1">{step.title}</h4>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="md:w-1/2 bg-gray-800 rounded-xl border border-gray-700 p-8">
            <h3 className="text-xl font-bold mb-6 text-center">Fair & Impartial Arbitration</h3>
            <div className="relative">
              <img src="/api/placeholder/500/300" alt="Dispute Resolution Process" className="rounded-lg w-full"/>
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-green-500 to-blue-600 opacity-20 rounded-lg"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FaHandshake className="text-white text-6xl" />
              </div>
            </div>
            <div className="mt-6 text-gray-400 text-sm">
              <p className="mb-2">Our arbitration system ensures:</p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                  <span>Impartial third-party review of all evidence</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                  <span>Transparent decision-making process</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                  <span>Fast resolution to minimize delays</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                  <span>Fair outcomes based on evidence and agreement terms</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="bg-gray-800 bg-opacity-50 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <p className="text-gray-300">
              Find answers to common questions about our escrow service. If you have additional questions, please contact our support team.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faqData.map((faq) => (
              <div 
                key={faq.id} 
                className="mb-4 border border-gray-700 rounded-lg overflow-hidden"
              >
                <button
                  className="w-full flex justify-between items-center p-6 text-left focus:outline-none"
                  onClick={() => toggleQuestion(faq.id)}
                >
                  <h3 className="text-lg font-semibold">{faq.question}</h3>
                  {activeQuestion === faq.id ? (
                    <FaAngleUp className="text-gray-400" />
                  ) : (
                    <FaAngleDown className="text-gray-400" />
                  )}
                </button>
                {activeQuestion === faq.id && (
                  <div className="p-6 pt-0 text-gray-400 border-t border-gray-700">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-green-600 to-blue-700 rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Transactions?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of users who trust Escrownia for secure, transparent, and efficient transactions.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link
              to="/"
              className="px-8 py-4 rounded-lg text-lg font-semibold bg-white text-gray-900 hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              Get Started Now
            </Link>
            <a
              href="#"
              className="px-8 py-4 rounded-lg text-lg font-semibold border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-300 shadow-lg"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>
      
      {/* Networks & Compatibility */}
      <section className="container mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold mb-4">Supported Networks</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Escrownia works across multiple blockchain networks, providing flexibility and convenience for all your transactions.
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-8">
          <div className="bg-gray-800 p-6 rounded-lg flex flex-col items-center max-w-xs">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center mb-4">
              <FaEthereum className="text-white text-3xl" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Ethereum</h3>
            <p className="text-gray-400 text-center text-sm">
              The original smart contract platform with the highest security and adoption.
            </p>
          </div>
          
          <div className="bg-gray-800 p-6 rounded-lg flex flex-col items-center max-w-xs">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 flex items-center justify-center mb-4">
              <svg className="text-white h-8 w-8" viewBox="0 0 32 32" fill="currentColor">
                <path d="M16 2L7 16l9 5 9-5-9-14z" />
                <path d="M16 25l-9-5 9 12 9-12-9 5z" />
                <path d="M16 21v-9l-9-5 9 14z" />
                <path d="M16 12v9l9-5-9-4z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Binance Smart Chain</h3>
            <p className="text-gray-400 text-center text-sm">
              Low fees and fast transactions make BSC perfect for smaller transactions.
            </p>
          </div>
          
          <div className="bg-gray-800 p-6 rounded-lg flex flex-col items-center max-w-xs">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center mb-4">
              <svg className="text-white h-8 w-8" viewBox="0 0 38 33" fill="currentColor">
                <path d="M19 0l19 33H0L19 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Polygon</h3>
            <p className="text-gray-400 text-center text-sm">
              Ethereum's leading Layer 2 scaling solution with fast and economical transactions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;